import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaBox, FaEnvelope, FaStar, FaChartLine, FaSignOutAlt } from 'react-icons/fa';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const password = localStorage.getItem('adminPassword');

  useEffect(() => {
    if (!password) {
      navigate('/admin');
      return;
    }
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats?password=${password}`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminPassword');
    navigate('/admin');
    toast.success('Logged out successfully');
  };

  const dashboardCards = [
    { title: 'Total Products', value: stats?.total_products || 0, icon: FaBox, color: 'bg-blue-500', link: '/admin/products' },
    { title: 'Total Enquiries', value: stats?.total_enquiries || 0, icon: FaEnvelope, color: 'bg-green-500', link: '/admin/enquiries' },
    { title: 'New Enquiries', value: stats?.new_enquiries || 0, icon: FaChartLine, color: 'bg-yellow-500', link: '/admin/enquiries' },
    { title: 'Testimonials', value: stats?.total_testimonials || 0, icon: FaStar, color: 'bg-purple-500', link: '#' },
  ];

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-4 border-[#0F5132] border-t-transparent'></div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50' data-testid='admin-dashboard'>
      {/* Header */}
      <div className='bg-[#0F5132] text-white py-6 shadow-lg'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center'>
          <div>
            <h1 className='text-3xl font-bold'>Admin Dashboard</h1>
            <p className='text-gray-200 mt-1'>Shivangel Ayurveda & Nutraceuticals</p>
          </div>
          <button
            onClick={handleLogout}
            className='flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors'
          >
            <FaSignOutAlt />
            <span>Logout</span>
          </button>
        </div>
      </div>

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* Stats Cards */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
          {dashboardCards.map((card, index) => (
            <Link
              key={index}
              to={card.link}
              className='bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1'
            >
              <div className='flex items-center justify-between mb-4'>
                <div className={`w-12 h-12 ${card.color} rounded-lg flex items-center justify-center`}>
                  <card.icon className='text-white text-2xl' />
                </div>
              </div>
              <h3 className='text-gray-600 text-sm font-medium mb-1'>{card.title}</h3>
              <p className='text-3xl font-bold text-gray-800'>{card.value}</p>
            </Link>
          ))}
        </div>

        {/* Quick Actions */}
        <div className='bg-white rounded-xl shadow-lg p-8'>
          <h2 className='text-2xl font-bold text-[#0F5132] mb-6'>Quick Actions</h2>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <Link
              to='/admin/products'
              className='px-6 py-4 bg-[#0F5132] hover:bg-[#0D4228] text-white rounded-lg font-semibold text-center transition-colors'
            >
              Manage Products
            </Link>
            <Link
              to='/admin/enquiries'
              className='px-6 py-4 bg-[#C9A227] hover:bg-[#B8921F] text-white rounded-lg font-semibold text-center transition-colors'
            >
              View Enquiries
            </Link>
            <Link
              to='/'
              className='px-6 py-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold text-center transition-colors'
            >
              View Website
            </Link>
          </div>
        </div>

        {/* Email Status */}
        <div className='mt-8 bg-white rounded-xl shadow-lg p-6'>
          <h3 className='text-xl font-bold text-[#0F5132] mb-4'>System Status</h3>
          <div className='flex items-center space-x-3'>
            <div className={`w-3 h-3 rounded-full ${stats?.email_enabled ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className='text-gray-700'>
              Email Notifications: {stats?.email_enabled ? 'Enabled' : 'Disabled (Add Resend API key in backend/.env)'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;